import { VgApiResult, vgApiRequest, vgApiUpload } from "./vg-api.service";

// Mirrors backend ENUM_SYS_ContactType (CoreEnums/SYS_Enums.cs).
// Backend Is_Valid for IDcCreateContact / IDcUpdateOrgContact only accepts
// Customer or Supplier — Unknown / UserMember are rejected.
export const CONTACT_TYPE = {
  UNKNOWN: 1,
  USER_MEMBER: 2,
  CUSTOMER: 3,
  SUPPLIER: 4,
} as const;

// Mirrors backend ENUM_Contact_Acquisition (CoreEnums/SYS_Enums.cs).
// Validator rejects Unknown, so anything created via the UI defaults to
// ManualInput.
export const CONTACT_ACQUISITION = {
  UNKNOWN: 1,
  MANUAL_INPUT: 2,
  PLATFORM_ACQUISITION: 3,
} as const;

export type ContactDetail = {
  contactId: number;
  orgId: number;
  contactFirstName: string;
  contactLastName: string;
  emailAddress: string;
  contactTitle?: string;
  contactType?: number;
  cloudFileUrl?: string;
};

function pickContactPayload(data: Record<string, unknown> | null | undefined): Partial<ContactDetail> {
  if (!data) return {};
  return {
    contactId: Number(data.contactId ?? data.ContactId ?? 0),
    orgId: Number(data.orgId ?? data.OrgId ?? 0),
    contactFirstName: String(data.contactFirstName ?? data.ContactFirstName ?? ""),
    contactLastName: String(data.contactLastName ?? data.ContactLastName ?? ""),
    emailAddress: String(data.emailAddress ?? data.EmailAddress ?? ""),
    contactTitle: data.contactTitle != null ? String(data.contactTitle) : undefined,
    cloudFileUrl: data.cloudFileUrl != null ? String(data.cloudFileUrl) : undefined,
  };
}

export async function createOrgContact(
  accessToken: string,
  payload: {
    orgId: number;
    contactFirstName: string;
    contactLastName: string;
    emailAddress: string;
    contactTitle?: string;
    contactType?: number;
    contactAcquisition?: number;
  }
): Promise<VgApiResult<{ newRecordID?: number; NewRecordID?: number }>> {
  return vgApiRequest(accessToken, "Create_Org_Contact", {
    orgId: payload.orgId,
    // Backend Is_Valid for IDcCreateContact requires userId == 0 (not the
    // default -1) when creating an org-scoped, non-user contact.
    userId: 0,
    contactFirstName: payload.contactFirstName,
    contactLastName: payload.contactLastName,
    emailAddress: payload.emailAddress,
    contactTitle: payload.contactTitle ?? "",
    contactType: payload.contactType ?? CONTACT_TYPE.CUSTOMER,
    // Validator rejects Unknown; UI-driven creates are ManualInput.
    contactAcquisition: payload.contactAcquisition ?? CONTACT_ACQUISITION.MANUAL_INPUT,
  });
}

export async function readContact(
  accessToken: string,
  orgId: number,
  contactId: number
): Promise<VgApiResult<ContactDetail>> {
  const { data, error } = await vgApiRequest<Record<string, unknown>>(accessToken, "Read_Contact", {
    orgId,
    contactId,
  });
  if (error) return { data: null, error };
  const p = pickContactPayload(data);
  return {
    data: {
      contactId: p.contactId ?? contactId,
      orgId: p.orgId ?? orgId,
      contactFirstName: p.contactFirstName ?? "",
      contactLastName: p.contactLastName ?? "",
      emailAddress: p.emailAddress ?? "",
      contactTitle: p.contactTitle,
      cloudFileUrl: p.cloudFileUrl,
    },
    error: null,
  };
}

export async function updateOrgContact(
  accessToken: string,
  payload: {
    orgId: number;
    contactId: number;
    contactFirstName: string;
    contactLastName: string;
    emailAddress: string;
    contactTitle?: string;
    contactType?: number;
    contactAcquisition?: number;
  }
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Update_Contact", {
    orgId: payload.orgId,
    contactId: payload.contactId,
    // Backend Is_Valid for IDcUpdateOrgContact requires userId == 0 (not the
    // default -1) when updating an org-scoped, non-user contact.
    userId: 0,
    contactFirstName: payload.contactFirstName,
    contactLastName: payload.contactLastName,
    emailAddress: payload.emailAddress,
    contactTitle: payload.contactTitle ?? "",
    contactType: payload.contactType ?? CONTACT_TYPE.CUSTOMER,
    // Validator rejects Unknown; UI-driven updates default to ManualInput.
    contactAcquisition: payload.contactAcquisition ?? CONTACT_ACQUISITION.MANUAL_INPUT,
  });
}

export async function deleteOrgContact(
  accessToken: string,
  orgId: number,
  contactId: number
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Delete_Contact", { orgId, contactId });
}

export type ContactImageUploadResult = {
  cloudFileUrl?: string;
};

function pickCloudFileUrl(data: Record<string, unknown> | null | undefined): string | undefined {
  if (!data) return undefined;
  const url =
    data.cloudFileUrl ??
    data.CloudFileUrl ??
    data.fileUrl ??
    data.FileUrl ??
    data.url ??
    data.Url;
  return url != null ? String(url) : undefined;
}

/**
 * Multipart upload to UploadContactImage. The controller binds Params in this
 * order: ["orgId", "contactId"] (see BaseDotNetCoreWebApiController.cs).
 */
export async function uploadContactImage(
  accessToken: string,
  orgId: number,
  contactId: number,
  file: File
): Promise<VgApiResult<ContactImageUploadResult>> {
  const { data, error } = await vgApiUpload<Record<string, unknown>>(
    accessToken,
    "UploadContactImage",
    [file],
    [orgId, contactId]
  );
  if (error) return { data: null, error };
  return {
    data: { cloudFileUrl: pickCloudFileUrl(data) },
    error: null,
  };
}

export type ContactCompleteRead = {
  animalList: unknown[];
  addressList: unknown[];
};

function pluckListFromContactComplete(
  data: Record<string, unknown> | null | undefined,
  camel: string,
  pascal: string
): unknown[] {
  if (!data) return [];
  const list = data[camel] ?? data[pascal];
  return Array.isArray(list) ? list : [];
}

export async function readContactCompleteByContactId(
  accessToken: string,
  orgId: number,
  contactId: number
): Promise<VgApiResult<ContactCompleteRead>> {
  const { data, error } = await vgApiRequest<Record<string, unknown>>(
    accessToken,
    "ReadContactCompleteByContactID",
    { orgId, contactId }
  );
  if (error) return { data: null, error };
  return {
    data: {
      animalList: pluckListFromContactComplete(
        data ?? undefined,
        "animalList",
        "AnimalList"
      ),
      addressList: pluckListFromContactComplete(
        data ?? undefined,
        "addressList",
        "AddressList"
      ),
    },
    error: null,
  };
}
